import { getStorage, ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage'
import { useAuthStore } from '@/stores/authStore'
import { FIREBASE_READ_TIMEOUT_MS } from '@/types/Constants'

interface FilePondServerProcess {
  (
    fieldName: string,
    file: File,
    metadata: Record<string, unknown>,
    load: (url: string) => void,
    error: (err: unknown) => void,
    progress: (percent: number) => void
  ): void
}

interface FilePondServerLoad {
  (source: string, load: (blob: Blob) => void, error?: (err: unknown) => void): void
}

interface FilePondServer {
  process: FilePondServerProcess
  load: FilePondServerLoad
}

/**
 * Routes uploads to `profileImages/{userId}/` when the FilePond uploader is the
 * user-profile widget, and to `Images/` for every other uploader (avatars, pack art, etc).
 * The `user-image-uploader` id is the contract between the Parameters view and this helper.
 */
function resolveStoragePath(fileName: string, uploaderId: string, userId?: string): string {
  const isProfileImage = uploaderId === 'user-image-uploader'
  return isProfileImage && userId ? `profileImages/${userId}/${fileName}` : `Images/${fileName}`
}

/**
 * @description Builds a FilePond `server` adapter that uploads files to Firebase Storage
 * with resumable transfer and live progress, then resolves the public download URL.
 * Wraps `uploadBytesResumable` and `getDownloadURL` behind the FilePond `process` / `load`
 * contract so the `FileUpload` component can plug Firebase in without knowing Storage details.
 *
 * @param uploaderId — Matches the `id` of the calling `FileUpload` instance. Passing
 * `'user-image-uploader'` routes the upload under the signed-in user's `profileImages/{uid}/`
 * folder; any other id routes to the shared `Images/` folder.
 * @param onUploadComplete — Called once with the resolved Firebase download URL after the
 * upload finishes. Use this to persist the URL on the owning Firestore document.
 *
 * @returns A `{ process, load }` object suitable for FilePond's `server` prop.
 *
 * @example
 * const filePondServer = useFirebaseStorage('user-image-uploader', (url) => {
 *   changeUserImage(user.uid, url, user.photoURL)
 * })
 */
export function useFirebaseStorage(
  uploaderId: string,
  onUploadComplete: (url: string) => void
): FilePondServer {
  const storage = getStorage()
  const authStore = useAuthStore()

  return {
    process: (_fieldName, file, _metadata, load, error, progress) => {
      const storagePath = resolveStoragePath(file.name, uploaderId, authStore.firebaseUser?.uid)
      const fileRef = ref(storage, storagePath)
      const uploadTask = uploadBytesResumable(fileRef, file)

      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const uploadProgress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100
          progress(uploadProgress)
        },
        (uploadError) => {
          error(uploadError)
        },
        async () => {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref)
          load(downloadURL)
          onUploadComplete(downloadURL)
        }
      )
    },
    load: (source, load, error) => {
      // AUD-28: bound the fetch with a timeout signal and surface failures to FilePond
      // (the previous version dropped errors silently, leaving the uploader stuck).
      fetch(source, { signal: AbortSignal.timeout(FIREBASE_READ_TIMEOUT_MS) })
        .then((res) => {
          if (!res.ok) throw new Error(`Storage load failed: ${res.status}`)
          return res.blob()
        })
        .then(load)
        .catch((err) => {
          console.error('Storage load failed:', err)
          error?.(err)
        })
    }
  }
}
